/**
 * Utilitaire de calcul des forfaits par voyageur — fonction pure réutilisable.
 *
 * Source de vérité unique pour la règle tarifaire RAAV (iter 103b).
 * Utilisée à la fois par :
 *  - Le formulaire public (`RegistrationForm.js`) — recalcul live à chaque modif
 *  - Le panel admin mode édition (`RegistrationEditPanel.js`) — recalcul à la sauvegarde
 *
 * Règle "2 pleins tarifs par chambre" (Lloret/Espagne + tous voyages avec nuitée) :
 *  - Une chambre doit avoir 2 personnes payant plein tarif pour qu'un enfant 3-11 ans
 *    bénéficie de la réduction enfant.
 *  - Les bébés (<3 ans en bus / <2 ans en avion) bénéficient toujours de la réduction.
 *  - Optimisation 2 personnes : si une chambre = 1 adulte + 1 enfant ou bébé, on compare
 *    "2 pleins tarifs" vs "1 plein + suppl. chambre ind. + 1 réduit" et on prend le moins cher.
 *
 * Voyages à la journée (Turin, Grau-du-Roi) : règle simple — réduction enfant si âge < 12 ans.
 */

/**
 * Calcule l'âge en années entières d'un voyageur à la date de départ du voyage.
 * @param {string} ddn - Date de naissance au format JJ/MM/AAAA (ou ISO YYYY-MM-DD)
 * @param {string} dateDepart - Date de départ du voyage au format YYYY-MM-DD
 * @returns {number|null} Âge en années, ou null si invalide
 */
export const computeAgeAtDeparture = (ddn, dateDepart) => {
  if (!ddn || !dateDepart) return null;
  let dd, mm, yyyy;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(ddn)) {
    [dd, mm, yyyy] = ddn.split('/').map(Number);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(ddn)) {
    [yyyy, mm, dd] = ddn.split('-').map(Number);
  } else return null;
  const born = new Date(yyyy, mm - 1, dd);
  const ref = new Date(dateDepart);
  if (isNaN(born.getTime()) || isNaN(ref.getTime())) return null;
  let age = ref.getFullYear() - born.getFullYear();
  const m = ref.getMonth() - born.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < born.getDate())) age--;
  return age;
};

/**
 * Détermine la catégorie d'âge selon le type de voyage.
 * @param {number|null} age
 * @param {boolean} isAvion - true = bébé < 2 ans (norme aérienne), false = bébé < 3 ans
 * @returns {'baby'|'child'|'adult'}
 */
export const catFromAge = (age, isAvion = false) => {
  if (age === null || age === undefined) return 'adult';
  const babyThreshold = isAvion ? 2 : 3;
  if (age < babyThreshold) return 'baby';
  if (age < 12) return 'child';
  return 'adult';
};

/**
 * Récupère la config admin tarifaire pour une destination (avec fallbacks 0).
 */
const getAdminConfig = (destination, chambreIndOverrides) => {
  const adminCfg = chambreIndOverrides[destination.id] || {};
  return {
    childDisc: (adminCfg.reduction_3_11 !== null && adminCfg.reduction_3_11 !== undefined)
      ? parseFloat(adminCfg.reduction_3_11) : 0,
    babyDisc: (adminCfg.reduction_bebe !== null && adminCfg.reduction_bebe !== undefined)
      ? parseFloat(adminCfg.reduction_bebe) : 0,
  };
};

/**
 * Récupère le supplément chambre individuelle pour une destination.
 */
const getSingleSupp = (destination) => {
  const info = destination.chambreIndividuelle || {};
  return info.amount || 0;
};

/**
 * Calcule les forfaits par voyageur pour un voyage avec nuitée (Lloret, NY, etc.).
 *
 * @param {Object} params
 * @param {Object} params.destination - Document form_destinations
 * @param {Array}  params.voyageurs   - [{nom, prenom, dateNaissance, ...}]
 * @param {Array}  params.chambres    - [{occupants: [vIdx], compagnons:[], typeChambre, ...}]
 * @param {Object} params.chambreIndOverrides - { [destId]: {reduction_3_11, reduction_bebe, ...} }
 * @returns {Object} { forfaits: {vIdx: {forfait, detail, category, categorieLabel, reduction}}, optimizedRoomsCount, totalForfaits }
 */
export const computeNuiteesPricing = ({ destination, voyageurs, chambres, chambreIndOverrides }) => {
  if (!destination) return { forfaits: {}, optimizedRoomsCount: 0, totalForfaits: 0 };
  const isAvion = destination.category === 'avion' || destination.avion;
  const baseGlobal = parseInt((destination.price || '').replace(/[^\d]/g, ''), 10) || 0;
  const { childDisc, babyDisc } = getAdminConfig(destination, chambreIndOverrides);
  const singleSupp = getSingleSupp(destination);
  if (baseGlobal <= 0) return { forfaits: {}, optimizedRoomsCount: 0, totalForfaits: 0 };

  const nyMatrix = destination.nyPricing || null;
  const resolveBasePrice = (ch, totalOcc) => {
    if (!nyMatrix) return baseGlobal;
    if (totalOcc === 1) return nyMatrix['grand-lit-2'] || baseGlobal;
    if (totalOcc === 2) {
      const type = ch.typeChambre || 'grand-lit';
      return nyMatrix[`${type}-2`] || baseGlobal;
    }
    if (totalOcc === 3) return nyMatrix['3-lits-3'] || baseGlobal;
    return baseGlobal;
  };

  const forfaits = {};
  let optimizedRoomsCount = 0;
  const optimizedRoomIndices = [];  // Iter 108 : exposer les idx de chambres

  (chambres || []).forEach((ch, chIdx) => {
    const occupantsIdx = (ch.occupants || []);
    const compagnons = Array.isArray(ch.compagnons)
      ? ch.compagnons.filter((c) => (c.nom || '').trim() && (c.prenom || '').trim())
      : [];
    const autresCount = (ch.autreOccupants || '').trim()
      ? (ch.autreOccupants || '').split(',').filter((n) => n.trim()).length
      : 0;
    const partageSoloCount = ch.partageSolo ? 1 : 0;
    if (occupantsIdx.length === 0) return;

    const allOccupants = [];
    occupantsIdx.forEach((vIdx) => {
      const v = voyageurs[vIdx];
      if (!v || !(v.nom || '').trim()) return;
      const age = computeAgeAtDeparture(v.dateNaissance, destination.dateDepart);
      allOccupants.push({ kind: 'voyageur', vIdx, age, cat: catFromAge(age, isAvion) });
    });
    compagnons.forEach((c, ci) => {
      const age = computeAgeAtDeparture(c.dateNaissance || '', destination.dateDepart);
      allOccupants.push({ kind: 'compagnon', ci, age, cat: age === null ? 'adult' : catFromAge(age, isAvion) });
    });
    for (let i = 0; i < autresCount; i++) allOccupants.push({ kind: 'anonyme-autre', age: null, cat: 'adult' });
    for (let i = 0; i < partageSoloCount; i++) allOccupants.push({ kind: 'anonyme-solo', age: null, cat: 'adult' });

    const basePrice = resolveBasePrice(ch, allOccupants.length);

    // Iter 107 — CHAMBRE INDIVIDUELLE RÉELLE (1 seul occupant)
    // Le supplément chambre individuelle s'applique automatiquement et le
    // voyageur paie plein tarif.
    // Miroir strict du comportement du formulaire public (useEffect
    // detectChambresIndividuelles → supplementChambre auto-calculé).
    if (allOccupants.length === 1 && singleSupp > 0) {
      optimizedRoomsCount += 1;
      optimizedRoomIndices.push(chIdx);
      const only = allOccupants[0];
      if (only.kind === 'voyageur') {
        const isChildFullTariff = only.cat === 'child';
        const isBaby = only.cat === 'baby';
        // Un bébé / enfant seul → plein tarif (car pas de partage possible)
        // Cas très rare en pratique mais géré proprement.
        forfaits[only.vIdx] = {
          forfait: basePrice,
          category: isBaby ? 'baby' : (isChildFullTariff ? 'child-full' : 'adult'),
          categorieLabel: isBaby
            ? 'Plein tarif (bébé seul en chambre) + supplément chambre individuelle'
            : (isChildFullTariff
              ? 'Plein tarif (3-11 ans seul en chambre) + supplément chambre individuelle'
              : 'Plein tarif + supplément chambre individuelle'),
          detail: `Plein tarif ${basePrice} € (suppl. chambre ind. de ${singleSupp} € ajouté au total)`,
          reduction: 0,
          optimized: true,
          singleRoomReal: true,  // drapeau explicite pour différencier du cas optimisation 2pers
        };
      }
      return;  // chambre traitée, pas d'autre logique
    }

    // Optimisation 2 personnes
    if (allOccupants.length === 2 && singleSupp > 0) {
      let best = null;
      for (let si = 0; si < 2; si++) {
        const oi = 1 - si;
        const other = allOccupants[oi];
        if (other.cat === 'adult') continue;
        const disc = other.cat === 'baby' ? babyDisc : childDisc;
        const altTotal = basePrice + singleSupp + Math.max(0, basePrice - disc);
        const standardTotal = 2 * basePrice;
        if (altTotal < standardTotal && (!best || altTotal < best.altTotal)) {
          best = { singleIdx: si, discountIdx: oi, disc, altTotal, discountCat: other.cat };
        }
      }
      if (best) {
        optimizedRoomsCount += 1;
        optimizedRoomIndices.push(chIdx);
        const singleOcc = allOccupants[best.singleIdx];
        const discountOcc = allOccupants[best.discountIdx];
        if (singleOcc.kind === 'voyageur') {
          forfaits[singleOcc.vIdx] = {
            forfait: basePrice,
            category: 'adult',
            categorieLabel: 'Plein tarif + supplément chambre individuelle (plus avantageux)',
            detail: `Plein tarif ${basePrice} € (suppl. chambre ind. de ${singleSupp} € ajouté au total)`,
            reduction: 0,
            optimized: true,
          };
        }
        if (discountOcc.kind === 'voyageur') {
          forfaits[discountOcc.vIdx] = {
            forfait: Math.max(0, basePrice - best.disc),
            category: best.discountCat,
            categorieLabel: best.discountCat === 'baby' ? 'Tarif moins de 3 ans' : 'Tarif 3-11 ans',
            detail: `Plein ${basePrice} € − Réduction ${best.discountCat === 'baby' ? 'bébé' : 'enfant'} ${best.disc} €`,
            reduction: best.disc,
            optimized: true,
          };
        }
        return;
      }
    }

    // Règle "2 slots plein tarif"
    const nonBabies = allOccupants.filter((o) => o.cat !== 'baby');
    nonBabies.sort((a, b) => ((b.age ?? 99) - (a.age ?? 99)));
    const isTwoPersonRoom = allOccupants.length === 2;
    let fullTariffAssigned = 0;
    nonBabies.forEach((k) => {
      if (fullTariffAssigned < 2) {
        if (k.kind === 'voyageur') {
          const isChildFullTariff = k.cat === 'child';
          forfaits[k.vIdx] = {
            forfait: basePrice,
            category: isChildFullTariff ? 'child-full' : 'adult',
            categorieLabel: isChildFullTariff
              ? (isTwoPersonRoom
                  ? 'Tarif 3-11 ans appliqué au plein tarif (règle 2 pleins tarifs par chambre)'
                  : 'Plein tarif (3-11 ans — occupe un slot adulte)')
              : 'Plein tarif (12 ans et +)',
            detail: `Plein tarif ${basePrice} €`,
            reduction: 0,
          };
        }
        fullTariffAssigned += 1;
      } else {
        if (k.kind !== 'voyageur') return;
        if (k.cat === 'child') {
          forfaits[k.vIdx] = {
            forfait: Math.max(0, basePrice - childDisc),
            category: 'child',
            categorieLabel: 'Tarif 3-11 ans',
            detail: `Plein ${basePrice} € − Réduction enfant ${childDisc} €`,
            reduction: childDisc,
          };
        } else {
          forfaits[k.vIdx] = {
            forfait: basePrice,
            category: 'adult',
            categorieLabel: 'Plein tarif (12 ans et +)',
            detail: `Plein tarif ${basePrice} €`,
            reduction: 0,
          };
        }
      }
    });
    allOccupants.forEach((k) => {
      if (k.kind !== 'voyageur') return;
      if (k.cat === 'baby') {
        forfaits[k.vIdx] = {
          forfait: Math.max(0, basePrice - babyDisc),
          category: 'baby',
          categorieLabel: 'Tarif moins de 3 ans',
          detail: `Plein ${basePrice} € − Réduction bébé ${babyDisc} €`,
          reduction: babyDisc,
        };
      }
    });
  });

  // Voyageurs non attribués à une chambre → plein tarif par défaut
  voyageurs.forEach((v, vIdx) => {
    if (!(v.nom || '').trim()) return;
    if (forfaits[vIdx] === undefined) {
      forfaits[vIdx] = {
        forfait: baseGlobal,
        category: 'adult',
        categorieLabel: 'Plein tarif (12 ans et +)',
        detail: `Plein tarif ${baseGlobal} € (non attribué à une chambre)`,
        reduction: 0,
      };
    }
  });

  const totalForfaits = Object.values(forfaits).reduce((sum, f) => sum + (f.forfait || 0), 0);
  return { forfaits, optimizedRoomsCount, optimizedRoomIndices, totalForfaits, singleSupp };
};

/**
 * Calcule les forfaits pour un voyage à la journée (Turin, Grau-du-Roi).
 * Règle simple : âge < 12 ans → réduction enfant si configurée.
 */
export const computeJourneePricing = ({ destination, voyageurs, chambreIndOverrides }) => {
  if (!destination) return { forfaits: {}, totalForfaits: 0 };
  const baseGlobal = parseInt((destination.price || '').replace(/[^\d]/g, ''), 10) || 0;
  if (baseGlobal <= 0) return { forfaits: {}, totalForfaits: 0 };
  const { childDisc } = getAdminConfig(destination, chambreIndOverrides);

  const forfaits = {};
  voyageurs.forEach((v, vIdx) => {
    if (!(v.nom || '').trim()) return;
    const age = computeAgeAtDeparture(v.dateNaissance, destination.dateDepart);
    const isChild = age !== null && age < 12;
    forfaits[vIdx] = {
      forfait: isChild ? Math.max(0, baseGlobal - childDisc) : baseGlobal,
      category: isChild ? 'child' : 'adult',
      categorieLabel: isChild ? 'Tarif 3-11 ans (journée)' : 'Plein tarif',
      detail: isChild ? `Plein ${baseGlobal} € − Réduction enfant ${childDisc} €` : `Plein tarif ${baseGlobal} €`,
      reduction: isChild ? childDisc : 0,
    };
  });
  const totalForfaits = Object.values(forfaits).reduce((sum, f) => sum + (f.forfait || 0), 0);
  return { forfaits, totalForfaits };
};

/**
 * Façade unifiée : choisit le bon calcul selon la catégorie du voyage.
 *
 * @param {Object} params
 * @param {Object} params.destination - Document form_destinations
 * @param {Array}  params.voyageurs
 * @param {Array}  params.chambres
 * @param {Object} params.chambreIndOverrides
 * @returns {Object} { forfaits, totalForfaits, optimizedRoomsCount, singleSupp }
 */
export const computePricing = ({ destination, voyageurs, chambres, chambreIndOverrides = {} }) => {
  if (!destination) return { forfaits: {}, totalForfaits: 0, optimizedRoomsCount: 0, singleSupp: 0 };
  const cat = destination.category;
  if (cat === 'journee') {
    return { ...computeJourneePricing({ destination, voyageurs, chambreIndOverrides }), optimizedRoomsCount: 0, optimizedRoomIndices: [], singleSupp: 0 };
  }
  // avion + nuitees → même logique (chambres + règle 2 pleins tarifs)
  return computeNuiteesPricing({ destination, voyageurs, chambres: chambres || [], chambreIndOverrides });
};
